<template>
  <div
    v-if="visible"
    :class="['msg-attachment-preview', `variant-${variant}`, { 'has-video-player': showsVideoPlayer, 'has-upload-chip': showsUploadChip }]"
    @click.stop="openAttachment"
  >
    <div
      v-if="!localFile && presentation.stateText"
      class="attachment-doc-fallback attachment-state"
      :aria-disabled="true"
      data-test="attachment-state"
    >
      <font-awesome-icon :icon="['fas', 'file']" />
      <span>{{ presentation.stateText }}</span>
      <span v-if="variant !== 'compact'" class="attachment-state-name">{{ displayName }}</span>
    </div>
    <img
      v-else-if="displayKind === 'image' && imageSrc && (presentation.showImage || localObjectUrl)"
      :src="imageSrc"
      :alt="displayName"
      class="attachment-image"
    />
    <!-- A sent image while its ticket URL is being issued. Never an <img> at
         file.php meanwhile: that is a reference, not an address (decision D5). -->
    <div
      v-else-if="displayKind === 'image' && !localFile && presentation.showImage && remoteImagePending"
      class="attachment-doc-fallback attachment-image-loading"
      aria-busy="true"
      data-test="attachment-image-loading"
    >
      <font-awesome-icon :icon="['fas', 'spinner']" spin />
      <span v-if="variant !== 'compact'">{{ displayName }}</span>
    </div>
    <div v-else-if="displayKind === 'pdf'" class="attachment-pdf">
      <img v-if="pdfThumbSrc" :src="pdfThumbSrc" :alt="displayName" class="attachment-image" />
      <div v-else class="attachment-doc-fallback">
        <font-awesome-icon :icon="['fas', 'file-pdf']" />
        <span v-if="variant !== 'compact'">{{ displayName }}</span>
      </div>
    </div>
    <!-- A video plays only in a bubble, in the page, behind a click. The player
         owns its own state (probe result, released stream) and keys it on
         file_uid and scan_status itself, so a re-render that hands it an equal
         item as a new object leaves it alone. Any other place (compose, compact,
         a local File) gets the chip below, never an element or an object url. -->
    <attachment-video-player
      v-else-if="showsVideoPlayer"
      :attachment="item"
      :action="presentation.action"
    />
    <!-- A video being uploaded, in a composer: its name, size, progress and
         state, and what can be done about it. Still no element and no object
         url: the bytes are the uploader's, and nothing here plays them. -->
    <div
      v-else-if="showsUploadChip"
      :class="['video-upload-chip', `is-${upload.status}`]"
      data-test="video-upload-chip"
    >
      <font-awesome-icon :icon="['fas', 'file-video']" class="chip-icon" />
      <div class="chip-body">
        <div class="chip-head">
          <span class="chip-name" data-test="video-upload-name">{{ displayName }}</span>
          <span v-if="uploadSizeLabel" class="chip-size" data-test="video-upload-size">{{ uploadSizeLabel }}</span>
        </div>
        <div
          v-if="showsUploadBar"
          class="chip-bar"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          :aria-valuenow="upload.percent"
          data-test="video-upload-bar"
        >
          <div class="chip-bar-fill" :style="{ width: `${upload.percent}%` }"></div>
        </div>
        <span
          class="chip-text"
          :role="upload.status === 'failed' ? 'alert' : 'status'"
          data-test="video-upload-text"
        >{{ upload.text }}</span>
        <div v-if="uploadActions.length" class="chip-actions">
          <button
            v-if="uploadActions.includes('verify')"
            type="button"
            class="chip-action"
            data-test="video-upload-verify"
            @click.stop="$emit('upload-action', 'verify')"
          >Verify again</button>
          <button
            v-if="uploadActions.includes('retry')"
            type="button"
            class="chip-action"
            data-test="video-upload-retry"
            @click.stop="$emit('upload-action', 'retry')"
          >Retry</button>
          <button
            v-if="uploadActions.includes('cancel')"
            type="button"
            class="chip-action"
            data-test="video-upload-cancel"
            @click.stop="$emit('upload-action', 'cancel')"
          >Cancel</button>
        </div>
      </div>
    </div>
    <div
      v-else
      class="attachment-doc-fallback"
      :data-test="!localFile && presentation.action === 'download' ? 'attachment-download' : null"
    >
      <font-awesome-icon :icon="['fas', displayKind === 'video' ? 'file-video' : 'file']" />
      <span v-if="variant !== 'compact'">{{ displayName }}</span>
    </div>
  </div>
</template>

<script>
import {
  attachmentPresentation,
  formatAttachmentSize,
  getAttachmentKind,
  getAttachmentName,
  normalizeMessageAttachments,
  resolveAttachmentUrl,
  withServerState,
} from "@/utils/messageAttachments";
import { downloadKeyonFile } from "@/api/keyonFiles";
import { resolveFileUrl } from "@/api2/fileTickets";
import AttachmentVideoPlayer from "@/components/includes/attachments/AttachmentVideoPlayer.vue";

export default {
  name: "MessageAttachmentPreview",
  components: { AttachmentVideoPlayer },
  props: {
    // Full message row (uses first attachment via helpers when attachment prop is omitted).
    message: { type: Object, default: null },
    // One item from normalizeMessageAttachments: { name, file_uid, kind, scan_status }.
    attachment: { type: Object, default: null },
    // Local File before send (compose / reply preview).
    localFile: { type: [Object, File], default: null },
    variant: {
      type: String,
      default: "bubble",
      validator: (v) => ["compact", "bubble", "compose"].includes(v),
    },
    // The upload snapshot of a local video (videoUploader.js getSnapshot) in a
    // composer: with it the chip shows progress and offers cancel, verify and retry.
    upload: { type: Object, default: null },
  },
  emits: ["upload-action"],
  data() {
    return {
      localObjectUrl: "",
      pdfThumbSrc: "",
      // Bumps on reset so in-flight PDF renders can abandon stale results.
      pdfLoadToken: 0,
      // 'scan_pending' | 'unavailable' once a download said so (a verdict that
      // changed after the list was rendered); null otherwise.
      serverState: null,
      // The ticket URL a sent image loads from, and the file it was issued
      // for: a ticket reads once, so a re-render of the same file keeps it.
      remoteImageSrc: "",
      remoteImageUid: null,
      // A ticket for remoteImageUid has been asked for and not answered yet.
      remoteImagePending: false,
    };
  },
  computed: {
    item() {
      return this.attachment || normalizeMessageAttachments(this.message)[0] || null;
    },
    presentation() {
      if (this.localFile) {
        return {
          kind: this.displayKind,
          availability: "available",
          showImage: this.displayKind === "image",
          showPdfThumb: this.displayKind === "pdf",
          action: "open",
          stateText: null,
        };
      }
      return withServerState(attachmentPresentation(this.item, this.variant), this.serverState);
    },
    visible() {
      return !!(this.localFile || this.item?.file_uid || this.item?.name);
    },
    displayName() {
      if (this.localFile) return this.localFile.name;
      if (this.attachment) return getAttachmentName(this.attachment);
      return getAttachmentName(this.message);
    },
    displayKind() {
      if (this.localFile) {
        if (String(this.localFile.type || "").startsWith("image/")) return "image";
        if (
          String(this.localFile.type || "") === "application/pdf" ||
          /\.pdf$/i.test(this.localFile.name || "")
        ) {
          return "pdf";
        }
        // A video File is only ever named here: no object url, no element.
        if (
          String(this.localFile.type || "").startsWith("video/") ||
          getAttachmentKind({ name: this.localFile.name }) === "video"
        ) {
          return "video";
        }
        return "other";
      }
      return getAttachmentKind(this.item);
    },
    // A sent image loads from a single-use content ticket URL (an <img> cannot
    // send the bearer token), resolved once per file in refreshRemoteThumb.
    // `play` is only ever given to an available bubble video (a state or a
    // local File has another action), so this is the whole condition.
    showsVideoPlayer() {
      return !this.localFile && this.displayKind === "video" && this.presentation.action === "play";
    },
    // A local video with an upload to show. Without one (a place that does not
    // upload) it is the plain name chip, as before.
    showsUploadChip() {
      return !!this.localFile && !!this.upload && this.displayKind === "video";
    },
    showsUploadBar() {
      return ["uploading", "waiting_network", "paused_mfa", "finalizing", "ready"].includes(this.upload?.status);
    },
    uploadActions() {
      return Array.isArray(this.upload?.actions) ? this.upload.actions : [];
    },
    uploadSizeLabel() {
      return this.localFile ? formatAttachmentSize(this.localFile.size) : "";
    },
    // The ticket URL only: until it resolves (or if it is refused) there is no
    // src, and the template shows the placeholder or the file chip instead.
    imageSrc() {
      if (this.localObjectUrl) return this.localObjectUrl;
      return this.presentation.showImage ? this.remoteImageSrc : "";
    },
    openUrl() {
      if (this.localObjectUrl) return this.localObjectUrl;
      return resolveAttachmentUrl(this.item) || "";
    },
  },
  watch: {
    localFile: {
      immediate: true,
      handler(file) {
        this.resetLocalPreview();
        if (!file) return;
        if (this.displayKind === "image") {
          this.localObjectUrl = URL.createObjectURL(file);
        } else if (this.displayKind === "pdf") {
          this.renderPdfThumbFromFile(file);
        }
      },
    },
    attachment: {
      deep: true,
      immediate: true,
      handler() {
        if (this.localFile) return;
        this.serverState = null;
        this.refreshRemoteThumb();
      },
    },
    message: {
      deep: true,
      immediate: true,
      handler() {
        if (this.localFile || this.attachment) return;
        this.serverState = null;
        this.refreshRemoteThumb();
      },
    },
  },
  beforeUnmount() {
    this.resetLocalPreview();
  },
  methods: {
    // Only an available pdf gets a PDF.js thumbnail; `other`, pending and
    // unavailable items never fetch.
    refreshRemoteThumb() {
      if (this.presentation.showPdfThumb) {
        this.renderPdfThumbFromUrl(this.openUrl);
      } else {
        this.pdfThumbSrc = "";
      }
      this.refreshRemoteImage();
    },
    async refreshRemoteImage() {
      const uid = this.presentation.showImage && this.item ? this.item.file_uid || null : null;
      if (uid === this.remoteImageUid) return;
      this.remoteImageUid = uid;
      this.remoteImageSrc = "";
      this.remoteImagePending = !!uid;
      if (!uid) return;
      const resolved = await resolveFileUrl(resolveAttachmentUrl(this.item));
      if (this.remoteImageUid !== uid) return;
      this.remoteImagePending = false;
      if (resolved.ok) {
        this.remoteImageSrc = resolved.url;
      } else if (resolved.state === "scan_pending" || resolved.state === "unavailable") {
        this.serverState = resolved.state;
      }
    },
    resetLocalPreview() {
      if (this.localObjectUrl) {
        URL.revokeObjectURL(this.localObjectUrl);
        this.localObjectUrl = "";
      }
      this.pdfThumbSrc = "";
      this.pdfLoadToken += 1;
    },
    async renderPdfThumbFromFile(file) {
      const token = ++this.pdfLoadToken;
      const blobUrl = URL.createObjectURL(file);
      try {
        await this.renderPdfThumbFromUrl(blobUrl, token);
      } finally {
        URL.revokeObjectURL(blobUrl);
      }
    },
    // First-page PDF thumbnail via pdfRender; ignores results if a newer load started.
    async renderPdfThumbFromUrl(url, token = null) {
      if (!url) return;
      const loadToken = token ?? ++this.pdfLoadToken;
      try {
        const { openPdf } = await import("@/utils/pdfRender");
        const width = this.variant === "compact" ? 56 : this.variant === "compose" ? 120 : 180;
        const pdf = await openPdf(url);
        if (loadToken !== this.pdfLoadToken) {
          pdf.destroy();
          return;
        }
        const canvas = await pdf.renderPage(1, { width });
        pdf.destroy();
        if (loadToken !== this.pdfLoadToken) return;
        this.pdfThumbSrc = canvas.toDataURL("image/png");
      } catch (err) {
        console.warn("PDF preview failed:", err);
        if (loadToken === this.pdfLoadToken) {
          this.pdfThumbSrc = "";
        }
      }
    },
    openAttachment() {
      if (this.localFile) {
        if (this.localObjectUrl) window.open(this.localObjectUrl, "_blank", "noopener,noreferrer");
        return;
      }
      const { action } = this.presentation;
      // `play` belongs to the player's own button: the page never opens a video
      // in another window (a direct navigation to file.php is a dead page).
      if (action === "none" || action === "play") return;
      // An `other` item can only reach 'download' (attachmentPresentation never
      // gives it 'open'); keyon also forces it to an attachment, so this is
      // defence in depth, not the only guard.
      if (action === "download") {
        this.downloadAttachment();
        return;
      }
      const url = resolveAttachmentUrl(this.item);
      if (url) window.open(url, "_blank", "noopener,noreferrer");
    },
    // Fetches through keyon so a 409/410 becomes the state on this item rather
    // than a saved error body. Never falls back to opening a window.
    async downloadAttachment() {
      const uid = this.item && this.item.file_uid;
      const res = await downloadKeyonFile(uid, this.displayName || "attachment");
      if (!res.ok && (res.state === "scan_pending" || res.state === "unavailable")) {
        this.serverState = res.state;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.msg-attachment-preview {
  cursor: pointer;
}

.has-video-player {
  cursor: default;
}

.has-upload-chip {
  cursor: default;
}

.variant-compose.has-upload-chip {
  width: 320px;
  max-width: 100%;
}

.video-upload-chip {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 10px;
  font-size: 12px;
  color: inherit;
}

.chip-icon {
  margin-top: 2px;
}

.chip-body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.chip-head {
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.chip-name {
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chip-size {
  opacity: 0.75;
  white-space: nowrap;
}

.chip-bar {
  height: 6px;
  border-radius: 3px;
  background: rgba(0, 0, 0, 0.15);
  overflow: hidden;
}

.chip-bar-fill {
  height: 100%;
  background: #4b5df0;
  transition: width 0.2s linear;
}

.is-failed .chip-text {
  color: #b42318;
}

.is-ready .chip-bar-fill {
  background: #2e7d32;
}

.chip-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.chip-action {
  border: 1px solid currentColor;
  border-radius: 4px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  font-size: 12px;
  padding: 2px 8px;
}

.variant-bubble.has-video-player {
  width: 320px;
  max-width: 100%;
}

.variant-compact {
  width: 44px;
  height: 44px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  background: #eef1ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.variant-bubble,
.variant-compose {
  margin-top: 8px;
  max-width: 220px;
  min-height: 40px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.15);
}

.attachment-image {
  display: block;
  width: 100%;
  height: auto;
  object-fit: cover;
}

.variant-compact .attachment-image {
  width: 44px;
  height: 44px;
  object-fit: cover;
}

.variant-bubble .attachment-image,
.variant-compose .attachment-image {
  max-height: 180px;
  object-fit: contain;
  background: #f8f9fa;
}

.attachment-state {
  cursor: default;
  opacity: 0.75;
}

.attachment-doc-fallback {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  font-size: 12px;
  color: inherit;
}

.variant-compact .attachment-doc-fallback {
  padding: 0;
  font-size: 16px;
}
</style>
